import { formatMoney } from "utils/money";

/**
 * Turns a failed `editOrder` result into one Arabic sentence to show the user.
 *
 * WHY THIS EXISTS: `useOrder#editOrder` and `useDeliveryOrder#editOrder` report
 * a failure by RETURNING the axios error rather than throwing it. Every caller
 * that ignored the return value therefore failed in complete silence — the row
 * showed the optimistic value, the rollback refetch snapped it back, and no
 * message appeared anywhere. Assigning a driver with an empty wallet looked
 * exactly like a click that never registered.
 *
 * The API always sends a ready Arabic sentence in `message`, so there is
 * nothing to translate here. It also spreads a refusal's numbers flat on the
 * body (`util/httpError.js`), which is why the wallet case can add the two
 * figures the admin needs to act: how much the driver has, and how much the
 * order costs him.
 *
 * @param {*} result - Whatever `editOrder` returned.
 * @param {string} fallback - Arabic sentence to show when the server sent none.
 * @returns {string|null} The sentence to show, or null when the call succeeded.
 */
export const readApiError = (result, fallback) => {
    const isFailure = result instanceof Error || result?.response?.status >= 400;
    if (!isFailure) return null;

    const body = result?.response?.data;
    // `result.message` is deliberately NOT a fallback: on an axios error it is
    // the English "Request failed with status code 400", which is worse than
    // the caller's own Arabic sentence.
    const message = body?.message || fallback;

    if (body?.code === "INSUFFICIENT_WALLET_BALANCE") {
        return `${message} (الرصيد ${formatMoney(body.balance)} · المطلوب ${formatMoney(body.required)})`;
    }

    return message;
};

/** What a list says when the server gave no answer at all: no network, or a timeout. */
export const NO_ANSWER_MESSAGE = "تعذر الاتصال بالخادم. تحقق من الاتصال ثم حاول مرة أخرى.";

/**
 * The sentence for a list that did not load.
 *
 * The API's own sentence when it answered. For a server error the request
 * layer already added the reference to it (api/requestReference.js). With no
 * answer, axios has only the English "Network Error", so this gives an Arabic
 * sentence in its place.
 *
 * @param {*} error - the error of the failed request, or nothing
 * @returns {string|null} null when there is no error
 */
export const readLoadError = (error) => {
    if (!error) return null;
    return error.response?.data?.message || NO_ANSWER_MESSAGE;
};
